import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EyeIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import ExperienceCard from "../../components/vendors/ExperienceCard";
import VendorInfo from "../../components/UserComponents/VendorInfo";
import {
  useAdminVendorApprove,
  useAdminVendorById,
  useAdminVendorSuspend,
} from "../../custom-hooks/queries";
import PageHeader from "../../components/common/PageHeader";
import CustomButton from "../../components/common/CustomButton";
import { getCleanedUrl } from "../../utils/HelperMethod";
import { toast } from "react-toastify";

/* ------------------ IMAGE VIEW MODAL ------------------ */
const ImageViewModal = ({ isOpen, onClose, imageSrc, title }) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-4 max-w-3xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 text-lg">
            ✕
          </button>
        </div>

        <img
          src={imageSrc}
          alt={title}
          className="max-h-[75vh] object-contain mx-auto"
        />
      </div>
    </div>
  );
};

/* ------------------ INPUT FIELD (READ ONLY) ------------------ */
const Field = ({ label, value, placeholder }) => (
  <div className="flex flex-col gap-1">
    <label className="text-sm font-medium text-gray-700">{label}</label>
    <input
      type="text"
      value={value || ""}
      placeholder={placeholder}
      disabled
      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 cursor-not-allowed"
    />
  </div>
);

/* ------------------ BUSINESS DETAILS CARD ------------------ */
const BusinessDetails = ({ vendor, govUrl, insuranceUrl, openImageModal }) => {
  if (!vendor) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-10">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Business Details
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label="Contact Name" value={vendor.contactName} />
        <Field label="Business Name" value={vendor.businessName} />
        <Field label="Email Address" value={vendor.email} />
        <Field label="Phone (Optional)" value={vendor.phone} />
        <Field label="Business Address" value={vendor.businessAddress} />
        <Field label="Tax ID" value={vendor.taxId} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        {/* Government ID */}
        <div className="flex items-center justify-between border rounded-lg px-4 py-3">
          <p className="text-sm font-medium text-gray-700">Government ID</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openImageModal(govUrl, "Government ID")}
            >
              <EyeIcon className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer" />
            </button>

            <a href={govUrl} download>
              <ArrowDownTrayIcon className="w-5 h-5 text-gray-500 hover:text-gray-700" />
            </a>
          </div>
        </div>

        {/* Insurance ID */}
        <div className="flex items-center justify-between border rounded-lg px-4 py-3">
          <p className="text-sm font-medium text-gray-700">Insurance ID</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => openImageModal(insuranceUrl, "Insurance ID")}
            >
              <EyeIcon className="w-5 h-5 text-gray-500 hover:text-gray-700 cursor-pointer" />
            </button>

            <a href={insuranceUrl} download>
              <ArrowDownTrayIcon className="w-5 h-5 text-gray-500 hover:text-gray-700" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------ MAIN PAGE ------------------ */
export default function VendorProfile() {
  const navigate = useNavigate();
  const { vendorId } = useParams();

  const [modalState, setModalState] = useState({
    isOpen: false,
    imageSrc: null,
    title: "",
  });

  const { mutate: suspendVendor, isPending: suspending } =
    useAdminVendorSuspend();
  const { mutate: approveVendor, isPending: approving } =
    useAdminVendorApprove();
  const { data, isLoading } = useAdminVendorById(vendorId);

  const vendor = data?.data;

  const logoUrl = getCleanedUrl(vendor?.logoUrl);
  const insuranceUrl = getCleanedUrl(vendor?.insuranceIdUrl);
  const govUrl = getCleanedUrl(vendor?.governmentIdUrl);

  const openImageModal = (src, title) => {
    setModalState({
      isOpen: true,
      imageSrc: src,
      title,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      imageSrc: null,
      title: "",
    });
  };

  if (isLoading) return null;
  if (!vendor) return <p className="text-center py-20">Vendor not found</p>;

  return (
    <div className="container mx-auto px-4 py-1 sm:px-6 sm:py-1">
      {/* HEADER + ACTIONS */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
        <PageHeader title="Vendor Profile" />

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-5 w-full lg:w-auto">
          <CustomButton
            variant="secondary"
            onClick={() =>
              suspendVendor(
                {
                  vendorId,
                  reason: "Violation of platform policies",
                },
                {
                  onSuccess: () => {
                    toast.success("Vendor suspended successfully");
                  },
                  onError: (error) => {
                    console.error("Error suspending vendor:", error);
                    toast.error("Failed to suspend vendor. Please try again.");
                  },
                },
              )
            }
            disabled={suspending}
            loading={suspending}
            className="w-full sm:w-auto"
          >
            Suspend Vendor
          </CustomButton>
          {vendor?.experiences[0]?.status?.toLowerCase() !== "approved" && (
            <CustomButton
              variant="primary"
              onClick={() =>
                approveVendor(vendorId, {
                  onSuccess: () => {
                    toast.success("Vendor approved successfully");
                  },
                  onError: (error) => {
                    console.error("Error approving vendor:", error);
                    toast.error("Failed to approve vendor. Please try again.");
                  },
                })
              }
              disabled={approving}
              loading={approving}
              className="w-full sm:w-auto"
            >
              Approve Vendor
            </CustomButton>
          )}
        </div>
      </div>

      {/* VENDOR INFO */}
      <VendorInfo
        name={vendor.businessName}
        description={vendor.businessDescription}
        rating={vendor.rating}
        logoUrl={logoUrl}
        totalReviews={vendor.totalReviews}
      />

      {/* BUSINESS DETAILS */}
      <BusinessDetails
        vendor={vendor}
        govUrl={govUrl}
        insuranceUrl={insuranceUrl}
        openImageModal={openImageModal}
      />

      {/* IMAGE MODAL */}
      <ImageViewModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        imageSrc={modalState.imageSrc}
        title={modalState.title}
      />

      {/* EXPERIENCES GRID */}
      {vendor.experiences?.length > 0 ? (
        <div className="mt-10 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {vendor.experiences.map((item) => (
            <div
              key={item.experienceId}
              onClick={() => navigate(`/user/experience/${item.experienceId}`)}
              className="cursor-pointer transition hover:scale-[1.01] active:scale-[0.99]"
            >
              <ExperienceCard
                id={item.experienceId}
                img={item.imageUrl}
                title={item.title}
                location={item.location}
                price={item.price}
                time={item.duration}
                rating={item.rating}
                tag={item.status}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-10 sm:mt-12">
          No experiences found.
        </p>
      )}
    </div>
  );
}
